import React, { useState, useEffect, useCallback } from 'react';
import '../styles/ImageLightbox.css';

const ImageLightbox = ({ images, currentIndex, onClose }) => {
  const [index, setIndex] = useState(currentIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIndex(currentIndex);
    resetZoom();
  }, [currentIndex]);

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handlePrev = useCallback((e) => {
    e.stopPropagation();
    setIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
    resetZoom();
  }, [images.length]);

  const handleNext = useCallback((e) => {
    e.stopPropagation();
    setIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
    resetZoom();
  }, [images.length]);

  const handleZoomIn = (e) => {
    e.stopPropagation();
    setScale(prev => Math.min(prev + 0.3, 4));
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    setScale(prev => {
      const newScale = prev - 0.3;
      if (newScale < 1) {
        setPosition({ x: 0, y: 0 });
        return 1;
      }
      return newScale;
    });
  };

  const handleWheel = (e) => {
    e.stopPropagation();
    if (e.deltaY < 0) {
      setScale(prev => Math.min(prev + 0.1, 4));
    } else {
      setScale(prev => {
        const newScale = prev - 0.1;
        if (newScale < 1) {
          setPosition({ x: 0, y: 0 });
          return 1;
        }
        return newScale;
      });
    }
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, scale, dragStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') handlePrev(e);
    if (e.key === 'ArrowRight') handleNext(e);
    if (e.ctrlKey && e.key === '+') handleZoomIn(e);
    if (e.ctrlKey && e.key === '-') handleZoomOut(e);
    if (e.key === '0') resetZoom();
  }, [onClose, handlePrev, handleNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const transformStyle = {
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
  };

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose} aria-label="Закрыть">×</button>
        <div className="lightbox-image-container" onWheel={handleWheel}>
          <img
            src={images[index]}
            alt={`Фото ${index + 1}`}
            className="lightbox-image"
            style={transformStyle}
            onMouseDown={handleMouseDown}
            draggable={false}
          />
        </div>
        <div className="lightbox-controls">
          <button className="lightbox-zoom-out" onClick={handleZoomOut} aria-label="Уменьшить">−</button>
          <span className="lightbox-zoom-indicator">{Math.round(scale * 100)}%</span>
          <button className="lightbox-zoom-in" onClick={handleZoomIn} aria-label="Увеличить">+</button>
        </div>
        <button className="lightbox-prev" onClick={handlePrev} aria-label="Предыдущее">‹</button>
        <button className="lightbox-next" onClick={handleNext} aria-label="Следующее">›</button>
        {images.length > 1 && (
          <div className="lightbox-counter">{index + 1} / {images.length}</div>
        )}
        <div className="lightbox-hint">Колесико мыши — зум | Ctrl++ / Ctrl+- | 0 — сброс</div>
      </div>
    </div>
  );
};

export default ImageLightbox;